import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { useEffect } from "react";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { insertLenderFeeSchema, type LenderFee } from "@shared/schema";
import { z } from "zod";

const FEE_TYPE_LABELS: Record<string, string> = {
  application_fee: "Application Fee",
  establishment_fee: "Establishment Fee",
  valuation_fee: "Valuation Fee",
  legal_fee: "Legal Fee",
  service_fee: "Service Fee",
  exit_fee: "Exit Fee",
  variation_fee: "Variation Fee",
  discharge_fee: "Discharge Fee",
};

const FEE_FREQUENCY_LABELS: Record<string, string> = {
  one_time: "One-time",
  monthly: "Monthly",
  quarterly: "Quarterly",
  annual: "Annual",
};

// Fee types that typically have a recurring frequency
const RECURRING_FEE_TYPES = new Set(["service_fee"]);

// Extend schema with percentage validation (max 100%)
const feeFormSchema = insertLenderFeeSchema.extend({
  percentageAmount: z.string().optional().refine(
    (val) => {
      if (!val || val === "") return true;
      const num = parseFloat(val);
      if (isNaN(num)) return false;
      return num >= 0 && num <= 100;
    },
    { message: "Percentage must be between 0 and 100" }
  ),
});

type FeeFormData = z.infer<typeof feeFormSchema>;

interface FeeFormModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  feeGroupId: string;
  fee?: LenderFee;
  onFeeCreated?: (fee: LenderFee) => void;
}

export function FeeFormModal({
  open,
  onOpenChange,
  feeGroupId,
  fee,
  onFeeCreated,
}: FeeFormModalProps) {
  const { toast } = useToast();
  const isEdit = !!fee;

  const defaultValues: Partial<FeeFormData> = {
    feeGroupId,
    feeType: "application_fee",
    feeName: "",
    amountType: "fixed",
    fixedAmount: undefined,
    percentageAmount: undefined,
    description: "",
    isMandatory: false,
    feeFrequency: "one_time",
  };

  // Helper: Convert decimal to percentage for display (0.01 -> 1)
  const decimalToPercent = (value: string | null | undefined): string => {
    if (!value || value === "") return "";
    const decimal = parseFloat(value);
    if (isNaN(decimal)) return "";
    return (decimal * 100).toString();
  };

  // Helper: Convert percentage to decimal for storage (1 -> 0.01)
  const percentToDecimal = (value: string | null | undefined): string => {
    if (!value || value === "") return "";
    const percent = parseFloat(value);
    if (isNaN(percent)) return "";
    return (percent / 100).toFixed(4);
  };

  const form = useForm<FeeFormData>({
    resolver: zodResolver(feeFormSchema),
    defaultValues,
    mode: "onChange",
    criteriaMode: "all",
  });

  useEffect(() => {
    if (open) {
      if (fee) {
        // Only reset with form-relevant fields to avoid Zod strict mode validation errors
        // Database-only fields (id, createdAt, updatedAt, organisationId) cause silent validation failures
        form.reset({
          feeGroupId: fee.feeGroupId,
          feeType: fee.feeType,
          feeName: fee.feeName,
          amountType: fee.amountType,
          fixedAmount: fee.fixedAmount ?? undefined,
          percentageAmount: fee.percentageAmount ? decimalToPercent(fee.percentageAmount) : undefined, // Convert for display
          description: fee.description ?? "",
          isMandatory: fee.isMandatory ?? false,
          feeFrequency: fee.feeFrequency ?? "one_time",
        });
      } else {
        form.reset(defaultValues);
      }
    }
  }, [open, fee, form]);

  const createMutation = useMutation({
    mutationFn: async (data: FeeFormData) => {
      const dataWithDecimal = {
        ...data,
        percentageAmount: data.percentageAmount ? percentToDecimal(data.percentageAmount) : undefined, // Convert to decimal for storage
      };
      const response = await apiRequest("POST", `/api/crm/fee-groups/${feeGroupId}/fees`, dataWithDecimal);
      return response.json();
    },
    onSuccess: async (newFee: LenderFee) => {
      queryClient.refetchQueries({ queryKey: ["/api/crm/fee-groups", feeGroupId, "fees"] });
      toast({
        title: "Fee created",
        description: "The fee has been created successfully.",
      });
      onOpenChange(false);
      if (onFeeCreated) onFeeCreated(newFee);
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: FeeFormData) => {
      const dataWithDecimal = {
        ...data,
        percentageAmount: data.percentageAmount ? percentToDecimal(data.percentageAmount) : undefined, // Convert to decimal for storage
      };
      return apiRequest("PATCH", `/api/crm/lender-fees/${fee?.id}`, dataWithDecimal);
    },
    onSuccess: () => {
      queryClient.refetchQueries({ queryKey: ["/api/crm/fee-groups", feeGroupId, "fees"] });
      toast({
        title: "Fee updated",
        description: "The fee has been updated successfully.",
      });
      onOpenChange(false);
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const onSubmit = (data: FeeFormData) => {
    if (isEdit) {
      updateMutation.mutate(data);
    } else {
      createMutation.mutate(data);
    }
  };

  const isPending = createMutation.isPending || updateMutation.isPending;
  const amountType = form.watch("amountType");
  const feeType = form.watch("feeType");
  const showFrequency = RECURRING_FEE_TYPES.has(feeType || "");

  // Auto-set frequency when fee type changes
  useEffect(() => {
    if (!feeType) return;
    if (RECURRING_FEE_TYPES.has(feeType)) {
      const currentFreq = form.getValues("feeFrequency");
      if (!currentFreq || currentFreq === "one_time") {
        form.setValue("feeFrequency", "monthly");
      }
    } else {
      form.setValue("feeFrequency", "one_time");
    }
  }, [feeType, form]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit Fee" : "Add Fee"}</DialogTitle>
          <DialogDescription>
            {isEdit ? "Update fee information" : "Create a new fee"}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="feeType"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Fee Type *</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value || undefined}>
                      <FormControl>
                        <SelectTrigger data-testid="select-fee-type">
                          <SelectValue placeholder="Select type" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {Object.entries(FEE_TYPE_LABELS).map(([value, label]) => (
                          <SelectItem key={value} value={value}>{label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="feeName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Fee Name *</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        placeholder="Application Fee"
                        data-testid="input-fee-name"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {showFrequency && (
                <FormField
                  control={form.control}
                  name="feeFrequency"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Frequency *</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value || "one_time"}>
                        <FormControl>
                          <SelectTrigger data-testid="select-fee-frequency">
                            <SelectValue placeholder="Select frequency" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {Object.entries(FEE_FREQUENCY_LABELS).filter(([v]) => v !== "one_time").map(([value, label]) => (
                            <SelectItem key={value} value={value}>{label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              <FormField
                control={form.control}
                name="amountType"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Amount Type *</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value || undefined}>
                      <FormControl>
                        <SelectTrigger data-testid="select-amount-type">
                          <SelectValue placeholder="Select type" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="fixed">Fixed Amount</SelectItem>
                        <SelectItem value="percentage">Percentage</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {amountType === "fixed" && (
                <FormField
                  control={form.control}
                  name="fixedAmount"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Fixed Amount</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          value={field.value || ""}
                          type="number"
                          step="0.01"
                          placeholder="500.00"
                          data-testid="input-fixed-amount"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              {amountType === "percentage" && (
                <FormField
                  control={form.control}
                  name="percentageAmount"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Percentage Amount</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          value={field.value || ""}
                          type="number"
                          step="0.01"
                          placeholder="1.00"
                          data-testid="input-percentage-amount"
                        />
                      </FormControl>
                      <FormDescription className="text-xs">
                        Enter as percentage (e.g., 1 for 1%)
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}
            </div>

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <Textarea
                      {...field}
                      value={field.value || ""}
                      placeholder="Fee description..."
                      data-testid="input-description"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="isMandatory"
              render={({ field }) => (
                <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                  <FormControl>
                    <Checkbox
                      checked={field.value || false}
                      onCheckedChange={field.onChange}
                      data-testid="checkbox-is-mandatory"
                    />
                  </FormControl>
                  <div className="space-y-1 leading-none">
                    <FormLabel>Mandatory fee</FormLabel>
                    <FormDescription>
                      This fee must be paid with every application
                    </FormDescription>
                  </div>
                </FormItem>
              )}
            />

            <DialogFooter className="gap-2 mt-6">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isPending}
                data-testid="button-cancel"
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isPending} data-testid="button-save">
                {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {isEdit ? "Update" : "Create"} Fee
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
